import axiosInstance from './axiosInstance'

const PROVINCE_BASE = '/provinces'

let provincesCache = null
let cacheTimestamp = null
const CACHE_TTL = 5 * 60 * 1000 // 5 minutos

/**
 * Invalida el cache de provincias
 */
export const invalidateProvincesCache = () => {
  provincesCache = null
  cacheTimestamp = null
}

/**
 * Obtiene todas las provincias (para administración)
 * @returns {Promise<Array>} Lista de provincias
 */
export const fetchAllProvinces = async (options = {}) => {
  const { force = false, noCache = false } = options
  const bypassCache = force || noCache
  const now = Date.now()

  if (!bypassCache && provincesCache && cacheTimestamp && now - cacheTimestamp < CACHE_TTL) {
    return provincesCache
  }

  try {
    const requestConfig = {}
    if (noCache) {
      requestConfig.headers = {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
        Expires: '0',
      }
      requestConfig.params = { _ts: now }
    }

    const response = await axiosInstance.get(PROVINCE_BASE, requestConfig)
    const rawData = response.data.provincias || response.data.res || []
    const mapped = rawData.map(p => ({
      id: p.id || p.id_pv,
      nombre: p.nombre,
      idPais: p.idPais || p.id_pais,
      habilitado: p.habilitado !== undefined ? Boolean(p.habilitado) : Boolean(p.habilitado?.data?.[0]),
    }))

    provincesCache = mapped
    cacheTimestamp = now
    return mapped
  } catch (error) {
    console.error('Error fetching provinces:', error)
    return provincesCache || []
  }
}

/**
 * Crea una nueva provincia
 * @param {Object} data - Datos de la provincia
 */
export const createProvince = async data => {
  try {
    const response = await axiosInstance.post(PROVINCE_BASE, data)
    invalidateProvincesCache()
    return { success: true, province: response.data.data }
  } catch (error) {
    console.error('Error creating province:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al crear la provincia',
    }
  }
}

/**
 * Actualiza una provincia existente
 * @param {number} id - ID de la provincia
 * @param {Object} data - Datos a actualizar
 */
export const updateProvince = async (id, data) => {
  try {
    const response = await axiosInstance.put(`${PROVINCE_BASE}/${id}`, data)
    invalidateProvincesCache()
    return { success: true, province: response.data.data }
  } catch (error) {
    console.error('Error updating province:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al actualizar la provincia',
    }
  }
}

/**
 * Elimina una provincia
 * @param {number} id - ID de la provincia
 */
export const deleteProvince = async id => {
  try {
    await axiosInstance.delete(`${PROVINCE_BASE}/${id}`)
    invalidateProvincesCache()
    return { success: true }
  } catch (error) {
    console.error('Error deleting province:', error)
    return {
      success: false,
      message: error.response?.data?.message || 'Error al eliminar la provincia',
    }
  }
}

/**
 * Habilita una provincia
 * @param {number} id - ID de la provincia
 */
export const enableProvince = async id => {
  try {
    const response = await axiosInstance.patch(`${PROVINCE_BASE}/${id}/enable`)
    invalidateProvincesCache()
    return { success: true, province: response.data.data }
  } catch (error) {
    console.error('Error enabling province:', error)
    return { success: false, message: 'Error al habilitar la provincia' }
  }
}

/**
 * Deshabilita una provincia
 * @param {number} id - ID de la provincia
 */
export const disableProvince = async id => {
  try {
    const response = await axiosInstance.patch(`${PROVINCE_BASE}/${id}/disable`)
    invalidateProvincesCache()
    return { success: true, province: response.data.data }
  } catch (error) {
    console.error('Error disabling province:', error)
    return { success: false, message: 'Error al deshabilitar la provincia' }
  }
}
